import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { useLocalstorageState } from "rooks";

import { getUnreadAlertCount } from "@/api";
import { useAuth } from "@/contexts/Auth/context";
import {
  Address,
  Alert,
  Bot,
  InsightMetric,
  InsightWidget,
  Project,
} from "@/types";

import { MonitorContext, monitorInitialState } from "./context";

const STORAGE_KEY = "sec3-monitor";

interface Props {
  children: React.ReactNode;
}

export const MonitorProvider = ({ children }: Props) => {
  const [value, set] = useLocalstorageState(STORAGE_KEY, monitorInitialState);
  const [currentProject, saveCurrentProject] =
    useLocalstorageState<Project | null>("sec3-current-project", null);
  const [currentAddress, setCurAddress] = useState<Address>();
  const [currentInsightsLayout, setCurrentInsightsLayout] = useState<
    InsightWidget[]
  >([]);
  const [currentAddressId, setCurAddressId] = useState("");
  const [newAlertCount, setNewAlertCount] = useState(0);
  const [currentBotId, setCurBotId] = useState("");
  const [currentAlertId, setCurAlertId] = useState("");
  const [currentProjectId, setCurProjectId] = useState("");
  const [botsData, setBotsData] = useState<Bot[]>([]);
  const [metricData, setMetricData] = useState<InsightMetric[]>([]);
  const [alertsData, setAlertsData] = useState<Alert[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [isLoadingBotsData, setIsLoadingBotsData] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Search-param setters do not queue updates, so keep a merged snapshot for
  // consecutive monitor state changes made before React renders again.
  const searchParamsRef = useRef(searchParams);
  if (searchParams.toString() !== searchParamsRef.current.toString()) {
    searchParamsRef.current = searchParams;
  }
  const [currentView, setCurrentView] = useState<
    "bots" | "alerts" | "insights"
  >("bots");
  const [currentMobileView, setCurrentMobileView] = useState<
    "bots" | "project"
  >("project");
  const [alertFrom, setAlertFrom] = useState("");
  const [alertTo, setAlertTo] = useState("");
  const [showOnlyAlertUnread, setShowOnlyAlertUnread] = useState(false);

  // Wipe every piece of user-scoped state back to its initial value.
  // Called explicitly on sign-out (via the effect below) so that when the
  // next user signs in the Monitor/WatchTower page cannot show stale
  // data from the previous account.
  const reset = useCallback(() => {
    setCurAddress(undefined);
    setCurrentInsightsLayout([]);
    setCurAddressId("");
    setNewAlertCount(0);
    setCurBotId("");
    setCurAlertId("");
    setCurProjectId("");
    setBotsData([]);
    setMetricData([]);
    setAlertsData([]);
    setProjects([]);
    setIsProjectLoading(false);
    setIsLoadingBotsData(false);
    setCurrentView("bots");
    setCurrentMobileView("project");
    setAlertFrom("");
    setAlertTo("");
    setShowOnlyAlertUnread(false);
    // Clear the persisted copies so that a page reload right after
    // logout does not rehydrate them.
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem("sec3-current-project");
    } catch {
      // ignore unavailable storage
    }
    saveCurrentProject(null);
  }, [saveCurrentProject]);

  // Auto-reset when the user signs out. AuthProvider sits above this
  // provider in the tree (see App.tsx), so we observe its state via
  // context instead of being called directly from logout().
  const { isLoggedIn } = useAuth();
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !isLoggedIn) {
      reset();
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, reset]);

  useEffect(() => {
    set(value);
    return () => {};
  }, [value, set]);

  useEffect(() => {
    if (currentAddress) {
      setIsLoadingBotsData(true);

      setIsLoadingBotsData(false);
    } else if (currentProjectId) {
      setIsLoadingBotsData(true);

      setIsLoadingBotsData(false);
    }
    return () => {};
  }, [currentAddress, currentProjectId]);

  const updateSearchParam = useCallback(
    (name: string, value: string) => {
      const nextSearchParams = new URLSearchParams(searchParamsRef.current);
      if (value) {
        nextSearchParams.set(name, value);
      } else {
        nextSearchParams.delete(name);
      }

      // Keep the frequently changing view at the end so changing tabs only
      // updates the URL suffix after the more stable scope parameters.
      const tab = nextSearchParams.get("tab");
      if (tab !== null) {
        nextSearchParams.delete("tab");
        nextSearchParams.set("tab", tab);
      }

      if (nextSearchParams.toString() === searchParamsRef.current.toString()) {
        return;
      }
      searchParamsRef.current = nextSearchParams;
      setSearchParams(nextSearchParams);
    },
    [setSearchParams],
  );

  const setCurrentBotId = (botId: string) => {
    setCurBotId(botId);
    updateSearchParam("botId", botId);
  };
  const setCurrentAlertId = (alertId: string) => {
    setCurAlertId(alertId);
    updateSearchParam("alertId", alertId);
  };
  const setCurrentProjectId = (projId: string) => {
    setCurProjectId(projId);
    updateSearchParam("projectId", projId);
  };
  const setCurrentAddressId = (addrId: string) => {
    setCurAddressId(addrId);
    updateSearchParam("addressId", addrId);
  };
  const setCurrentViewAndSearchParam = (
    view: "bots" | "alerts" | "insights",
  ) => {
    setCurrentView(view);
    updateSearchParam("tab", view);
  };
  const setCurrentAddress = (addr: Address) => {
    setCurAddress(addr);
    setCurrentAddressId(addr?.id || "");
  };

  const { refetch: refetchNewAlertCount } = useQuery<{ count: number }, Error>(
    ["new alerts count", currentAddressId, currentProjectId, currentView],
    () =>
      currentAddressId
        ? getUnreadAlertCount(currentAddressId, "addressId")
        : currentProjectId
          ? getUnreadAlertCount(currentProjectId, "projectId")
          : Promise.resolve(0),
    {
      keepPreviousData: true,
      initialData: { count: 0 },
      enabled: !!currentProjectId,
      onSuccess: (data) => {
        setNewAlertCount(data.count);
      },
    },
  );

  return (
    <MonitorContext.Provider
      value={{
        currentAddress,
        setCurrentAddress,
        currentProjectId,
        setCurrentProjectId,
        currentBotId,
        setCurrentBotId,
        currentProject,
        setCurrentProject: (e: Project) => saveCurrentProject(e),
        projects,
        setProjects,
        botsData,
        setBotsData,
        isLoadingBotsData,
        alertsData,
        setAlertsData,
        currentView,
        setCurrentView: setCurrentViewAndSearchParam,
        isProjectLoading,
        setIsProjectLoading,
        currentAddressId,
        setCurrentAddressId,
        currentMobileView,
        setCurrentMobileView,
        refetchNewAlertCount,
        newAlertCount,
        currentInsightsLayout,
        setCurrentInsightsLayout,
        metricData,
        setMetricData,
        currentAlertId,
        setCurrentAlertId,
        alertFrom,
        alertTo,
        setAlertFrom,
        setAlertTo,
        showOnlyAlertUnread,
        setShowOnlyAlertUnread,
        reset,
      }}
    >
      {children}
    </MonitorContext.Provider>
  );
};
